import React, { useEffect, useState } from 'react';
import dislikeIcon from '@assets/icons/bad-icon-png.png';
import likeIcon from '@assets/icons/good-icon-png.png';
import greyStarIcon from '@assets/icons/grey-star-icon.png';
import sadIcon from '@assets/icons/not-satisfactory-icon-png.png';
import happyIcon from '@assets/icons/satisfactory-icon-png.png';
import yellowStarIcon from '@assets/icons/yellow-star-icon.png';
import Types from 'FielderUtils/forms/types';
import { toDataURLWithJsZip } from 'FielderUtils/s3Proxy';
import { saveAs } from 'file-saver';
import moment from 'moment';
import MainTable from 'OldComponents/MainTable';
import Grid from '@mui/material/Grid2';
import { fixImageOrientation } from 'FielderUtils/fixImageOrientation';
import { loadingStop } from '@core/store/modules/loading/loading.slice';
import { Button, Box, TextField, Typography } from '@mui/material';

/**
 * Looks if a certain asset, inventory or form has a custom template
 * to be generated
 * @param {*} template - Asset, Inventory or Forms template
 * @returns {object} - containing the custom types to generate the
 * 						asset, inventory or forms template
 */
export const checkCustomTemplateType = (template) => {
  const customTemplateTypes = {
    xlsx: false,
    html: false,
  };
  if (template.category && template.category.templatePath) {
    switch (template.category.templateType) {
      case 'xlsx':
        customTemplateTypes.xlsx = true;
        break;
      case 'html':
        customTemplateTypes.html = true;
        break;
      default:
        break;
    }
  }
  return customTemplateTypes;
};
/**
 * Handles the image download from inside the modules (asset, inventory or forms)
 * @param {*} images
 * @param {*} index
 * @param {*} zip
 * @param {*} templateType
 * @param {*} dispatch
 */
const downloadZipFile = (zip, templateType, dispatch, taskData) => {
  const name = `${taskData.idFielder}-${templateType}`;
  zip.generateAsync({ type: 'blob' }).then((content) => {
    saveAs(content, `${name}.zip`);
    dispatch(
      loadingStop({ name: 'ZIP_DOWNLOAD' })
    );
  });
};
export const getImagesFromGroup = async (images, index, zip, templateType, dispatch, taskData) => {
  if (index < images.length) {
    const image = images[index];
    if (image) {
      const zipAux = await toDataURLWithJsZip(image, zip);
      const newIndex = index + 1;
      getImagesFromGroup(images, newIndex, zipAux, templateType, dispatch, taskData);
    } else downloadZipFile(zip, templateType, dispatch, taskData);
  } else downloadZipFile(zip, templateType, dispatch, taskData);
};
export const getCustomPDFBody = (templates, taskData, customTemplateType, language) => {
  const body = {
    idTask: taskData.task.idFielder,
    task: taskData.task,
    agent: { name: taskData.agent.name },
  };
  if (templates.form) {
    body.form = templates.form;
  } else if (templates.asset) {
    const bodyAsset = JSON.parse(JSON.stringify(templates.asset));
    bodyAsset.logs = [];
    bodyAsset.category = templates.asset.category.name;
    body.asset = bodyAsset;
  } else if (templates.inventory) {
    const bodyInventory = JSON.parse(JSON.stringify(templates.inventory));
    bodyInventory.logs = [];
    bodyInventory.category = templates.inventory.category.name;
    body.inventory = bodyInventory;
  } else return;
  if (!customTemplateType.html) body.lan = language;
  return body;
};
const getDate = (question, timezone) => {
  if (question.date) {
    question.dateFormat.replace('dd', 'DD');
    return moment(question.date).tz(timezone).format('DD/MM/YYYY hh:mm');
  }
  return '';
};
const EXIF_SUPPORTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/tiff',
]);
const OrientedImage = ({ src }) => {
  const [orientedUrl, setOrientedUrl] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const mimeType = (blob.type || '').toLowerCase();
        if (EXIF_SUPPORTED_MIME_TYPES.has(mimeType)) {
          const fixedUrl = await fixImageOrientation(blob);
          setOrientedUrl(fixedUrl);
        } else {
          const objectUrl = URL.createObjectURL(blob);
          setOrientedUrl(objectUrl);
        }
      } catch (err) {
        console.error('Error fixing image orientation', err);
        setOrientedUrl(src);
      }
    };

    if (src) fetchImage();
  }, [src]);

  if (!orientedUrl) return null;

  return (
    <Box
      component="img"
      src={orientedUrl}
      alt=""
      sx={{ maxWidth: 400, maxHeight: 400, display: 'block' }}
    />
  );
};
const renderQuestionContainer = (title, content, options = {}) => {
  const { titleWrap = true, fixedHeight } = options;
  return (
    <Grid container sx={{ p: 0.5, ml: 1, mr: 1 }}>
      <Grid size={12}>
        <Grid container>
          <Grid
            size={3}
            sx={{
              ...(titleWrap && { wordWrap: 'break-word' }),
              ...(fixedHeight && { height: fixedHeight }),
              display: 'flex',
              alignItems: 'flex-start',
              pr: 1,
            }}
          >
            <Typography variant="body2">{title}</Typography>
          </Grid>
          <Grid
            size={9}
            sx={{
              ...(fixedHeight && { height: fixedHeight }),
            }}
          >
            {content}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
const renderReadOnlyTextField = (value) => (
  <TextField
    value={value}
    variant="outlined"
    fullWidth
    multiline
    minRows={2}
    slotProps={{ input: { readOnly: true } }}
    sx={(theme) => ({
      '& .MuiInputBase-root': {
        alignItems: 'flex-start',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
      },
    })}
  />
);
const renderDashedBox = (children, options = {}) => {
  const { red = false, fullHeight } = options;
  return (
    <Box
      sx={(theme) => ({
        borderStyle: 'dashed',
        borderWidth: 2,
        borderRadius: 2,
        borderColor: red ? theme.palette.error.main : theme.palette.divider,
        minHeight: 30,
        p: 2,
        boxSizing: 'border-box',
        height: fullHeight ? '100%' : 'auto',
      })}
    >
      {children}
    </Box>
  );
};
export const getFieldQuestion = (question, showFormFile, timezone, lan, getTextFile, isField) => {
  try {
    let text = '';
    switch (parseInt(question.type)) {
      case Types.MULTIPLE_CHOICE: {
        const selected = [];
        question.options.some((opt) => {
          if (opt.checked === true) {
            selected.push(opt.name);
            if (question.singleAnswer === true) return true;
          }
        });
        text = selected.join(', ');
        break;
      }
      case Types.TEXT_FIELD:
        text = question.text;
        break;
      case Types.NUMERIC_RATING:
        text = question.value;
        break;
      case Types.BINARY_RATING:
        if (question.iconType === 'FINGERS') {
          question.value === true ? (text = likeIcon) : (text = dislikeIcon);
        } else {
          question.value === true ? (text = happyIcon) : (text = sadIcon);
        }
        break;
      case Types.STARS_RATING:
        text = question.value;
        break;
      case Types.ELECTRONIC_SIGNATURE:
        text = question.file;
        break;
      case Types.FILES:
        text = question.file;
        break;
      case Types.CODEBAR_IMG:
        text = question.file;
        break;
      case Types.CODEBAR_TEXT:
        text = question.value;
        break;
      case Types.DATE_HOUR:
        text = question.parsedDate ? question.parsedDate : getDate(question, timezone);
        if (text != null) {
          text = text.replace('January', lan.january);
          text = text.replace('February', lan.february);
          text = text.replace('March', lan.march);
          text = text.replace('April', lan.april);
          text = text.replace('May', lan.may);
          text = text.replace('June', lan.june);
          text = text.replace('July', lan.july);
          text = text.replace('August', lan.august);
          text = text.replace('September', lan.september);
          text = text.replace('October', lan.october);
          text = text.replace('November', lan.november);
          text = text.replace('December', lan.december);
        } else {
          text = '';
        }
        break;
      case Types.TEXT_FILE:
        if (question.file) {
          if (showFormFile[question.id]) {
            text = (
              <TextField
                value={showFormFile[question.id]}
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                slotProps={{ input: { readOnly: true } }}
                sx={(theme) => ({
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                })}
              />
            );
          } else {
            text = (
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => getTextFile(question, isField)}
              >
                {lan.viewFileContent}
              </Button>
            );
          }
        } else {
          text = (
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              disabled
            >
              {lan.noFileFound}
            </Button>
          );
        }
        break;
      case Types.CATALOGUE: {
        const columns = [
          { id: 'id', padding: '' },
          { id: 'option', padding: '' },
        ];
        if (question.includeValue) {
          columns.push({
            id: 'value',
            name: lan.value,
            padding: '',
          });
        }
        if (question.includeComment) {
          columns.push({
            id: 'comment',
            name: lan.comment,
            padding: '',
          });
        }
        const rows = question.options
          .map((opt) => {
            if (opt.checked) {
              return {
                id: opt.id,
                option: opt.name,
                value: opt.value ? opt.value : '',
                comment: opt.comment ? opt.comment : '',
              };
            }
            return null;
          })
          .filter((n) => n);

        return (
          <Grid container sx={{ mt: 4 }}>
            <Grid
              size={10}
              sx={{
                ml: 'auto',
                mr: 5,
              }}
            >
              <Grid container>
                <Grid
                  size={3}
                  sx={{
                    wordWrap: 'break-word',
                    pr: 1,
                  }}
                >
                  <Typography variant="body2">{question.title}</Typography>
                </Grid>
                <Grid size={9}>
                  {renderDashedBox(
                    rows && rows.length > 0 ? (
                      <MainTable
                        dense
                        disabled
                        fullItemsHeight
                        cols={columns}
                        rows={rows}
                        lan={lan}
                        height={500}
                      />
                    ) : null
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      }
      default:
        break;
    }
    if (text === null) text = '';
    if (parseInt(question.type) === Types.ELECTRONIC_SIGNATURE) {
      return renderQuestionContainer(
        question.title,
        renderDashedBox(
          <Box
            sx={{
              width: '100%',
              height: 200,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            {text !== '' && (
              <Box
                component="img"
                src={text}
                alt=""
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>,
          { fullHeight: true }
        )
      );
    }
    if (parseInt(question.type) === Types.CODEBAR_IMG) {
      return renderQuestionContainer(
        question.title,
        renderDashedBox(
          text !== '' ? (
            <Box
              component="img"
              src={text}
              alt=""
              sx={{
                maxWidth: '100%',
                maxHeight: 400,
                display: 'block',
                my: 2,
                mx: 'auto',
              }}
            />
          ) : null
        )
      );
    }
    if (parseInt(question.type) === Types.FILES) {
      return renderQuestionContainer(
        question.title,
        renderDashedBox(
          text !== '' ? <OrientedImage src={text} /> : null,
          { fullHeight: true }
        ),
        { fixedHeight: 400 }
      );
    }
    if (parseInt(question.type) === Types.STARS_RATING) {
      const stars = [];
      for (let i = 0; i < 5; i++) {
        if (i < text) {
          stars.push(
            <Box
              key={`star-${i}`}
              sx={{ ml: 1, display: 'inline-flex' }}
            >
              <Box
                component="img"
                src={yellowStarIcon}
                alt=""
                sx={{ maxHeight: 25 }}
              />
            </Box>
          );
        } else {
          stars.push(
            <Box
              key={`star-${i}`}
              sx={{ ml: 1, display: 'inline-flex' }}
            >
              <Box
                component="img"
                src={greyStarIcon}
                alt=""
                sx={{ maxHeight: 25 }}
              />
            </Box>
          );
        }
      }
      return renderQuestionContainer(
        question.title,
        renderDashedBox(
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              py: 1.25,
              maxWidth: '100%',
            }}
          >
            {stars}
          </Box>
        )
      );
    }
    if (parseInt(question.type) === Types.BINARY_RATING) {
      return renderQuestionContainer(
        question.title,
        renderDashedBox(
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1.25,
              pl: 1.5,
              maxWidth: '100%',
            }}
          >
            <Box
              component="img"
              src={text}
              alt=""
              sx={{ maxHeight: 25 }}
            />
          </Box>
        )
      );
    }
    if (parseInt(question.type) === Types.TEXT_FILE) {
      return renderQuestionContainer(
        question.title,
        <Box sx={{ ml: 1.5 }}>{text}</Box
        >
      );
    }

    // Default text response
    return renderQuestionContainer(
      question.title,
      renderReadOnlyTextField(text),
      { titleWrap: true }
    );
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const getTaskModulesFieldQuestion = (
  question,
  showFormFile,
  timezone,
  lan,
  getTextFile,
  isField
) => {
  const type = parseInt(question.type);

  // For everything except the TaskModules-specific types,
  // reuse the generic util logic.
  if (type !== Types.FILES && type !== Types.INVENTORY && type !== Types.ASSET) {
    return getFieldQuestion(question, showFormFile, timezone, lan, getTextFile, isField);
  }

  // ---------- FILES: keep TaskModules behaviour (with metadata) ----------
  if (type === Types.FILES) {
    const hasMetadata = !!question.enableMetadata;
    const title = question.title || '';
    const image = question.file || '';

    // No metadata -> oriented image in dashed box, fixed height
    if (!hasMetadata) {
      return renderQuestionContainer(
        title,
        renderDashedBox(
          image ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
              }}
            >
              <OrientedImage src={image} />
            </Box>
          ) : null,
          { fullHeight: true }
        ),
        { fixedHeight: 400 }
      );
    }

    // With metadata: reproduce the same logic as TaskModules (lat / lng / date)
    const meta = question.metaData || {};
    const timeZone = meta.timeZone || timezone;
    const formattedDate =
      meta.creationDate && timeZone
        ? moment.tz(meta.creationDate, timeZone).format('DD-MM-YYYY HH:mm')
        : lan.noData;
    return renderQuestionContainer(
      title,
      renderDashedBox(
        <Box sx={{ minHeight: 350, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, }}>
            {image ? <OrientedImage src={image} /> : null}
          </Box>

          <Grid container sx={{ mb: 1 }}>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center', fontWeight: 600 }}>
              {lan.latitude}
            </Grid>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center', fontWeight: 600 }}>
              {lan.longitude}
            </Grid>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center', fontWeight: 600 }}>
              {lan.date}
            </Grid>
          </Grid>
          <Grid container>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              {meta.latitude ?? lan.noData}
            </Grid>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              {meta.longitude ?? lan.noData}
            </Grid>
            <Grid size={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              {formattedDate}
            </Grid>
          </Grid>
        </Box>,
        { fullHeight: true }
      ),
      { fixedHeight: 400 }
    );
  }
  if (type === Types.INVENTORY) {
    const rows = [];
    if (question.inventoryId) rows.push({ label: lan.id, value: question.inventoryId });
    if (question.includeInventoryType) rows.push({ label: lan.type, value: question.inventoryType });
    if (question.includeName) rows.push({ label: lan.name, value: question.name });
    if (question.includeBrand) rows.push({ label: lan.brand, value: question.brand });
    if (question.includeModel) rows.push({ label: lan.model, value: question.model });
    if (question.includeSerialNumber) rows.push({ label: lan.serialNumber, value: question.serialNumber });
    return renderQuestionContainer(
      question.title,
      renderDashedBox(
        <Grid container spacing={0.5}>
          {rows.map((row, idx) => (
            <Grid size={12} id={`${question.id}-inventory-${idx}`}>
              <Box sx={{ display: 'flex' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, width: '35%' }}>
                  {row.label}:
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {row.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )
    );
  }
  if (type === Types.ASSET) {
    const rows = [];
    if (question.assetId) rows.push({ label: lan.id, value: question.assetId });
    if (question.includeAssetType) rows.push({ label: lan.type, value: question.assetType });
    if (question.includeName) rows.push({ label: lan.name, value: question.name });
    if (question.includeBrand) rows.push({ label: lan.brand, value: question.brand });
    if (question.includeModel) rows.push({ label: lan.model, value: question.model });
    if (question.includeSerialNumber) rows.push({ label: lan.serialNumber, value: question.serialNumber });
    return renderQuestionContainer(
      question.title,
      renderDashedBox(
        <Grid container spacing={0.5}>
          {rows.map((row, idx) => (
            <Grid size={12} id={`${question.id}-asset-${idx}`}>
              <Box sx={{ display: 'flex' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, width: '35%' }}>
                  {row.label}:
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {row.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )
    );
  }

  return null;
};
//Original code:
/*import React, { useEffect, useState } from 'react';
import dislikeIcon from '@assets/icons/bad-icon-png.png';
import likeIcon from '@assets/icons/good-icon-png.png';
import greyStarIcon from '@assets/icons/grey-star-icon.png';
import sadIcon from '@assets/icons/not-satisfactory-icon-png.png';
import happyIcon from '@assets/icons/satisfactory-icon-png.png';
import yellowStarIcon from '@assets/icons/yellow-star-icon.png';
import Types from 'FielderUtils/forms/types';
import { toDataURLWithJsZip } from 'FielderUtils/s3Proxy';
import { saveAs } from 'file-saver';
import moment from 'moment';
import MainTable from 'OldComponents/MainTable';
import TextareaAutosize from 'react-textarea-autosize';
import Grid from '@mui/material/Grid2';
import { fixImageOrientation } from 'FielderUtils/fixImageOrientation'
import { loadingStop } from '@core/store/modules/loading/loading.slice';
import { Button } from '@mui/material';
export const checkCustomTemplateType = (template) => {
  const customTemplateTypes = {
    xlsx: false,
    html: false,
  };
  if (template.category && template.category.templatePath) {
    switch (template.category.templateType) {
      case 'xlsx':
        customTemplateTypes.xlsx = true;
        break;
      case 'html':
        customTemplateTypes.html = true;
        break;
      default:
        break;
    }
  }
  return customTemplateTypes;
};
const downloadZipFile = (zip, templateType, dispatch, taskData) => {
  const name = `${taskData.idFielder}-${templateType}`;

  zip.generateAsync({ type: 'blob' }).then((content) => {
    saveAs(content, `${name}.zip`);
    dispatch(
      loadingStop({ name:"ZIP_DOWNLOAD" })
    );
  });
};
export const getImagesFromGroup = async (images, index, zip, templateType, dispatch, taskData) => {
  if (index < images.length) {
    const image = images[index];
    if (image) {
      const zipAux = await toDataURLWithJsZip(image, zip);
      const newIndex = index + 1;
      getImagesFromGroup(images, newIndex, zipAux, templateType, dispatch, taskData);
    } else downloadZipFile(zip, templateType, dispatch, taskData);
  } else downloadZipFile(zip, templateType, dispatch, taskData);
};
export const getCustomPDFBody = (templates, taskData, customTemplateType, language) => {
  const body = {
    idTask: taskData.task.idFielder,
    task: taskData.task,
    agent: { name: taskData.agent.name, },
  };
  if (templates.form) {
    body.form = templates.form;
    //body.request = 'DO_ACTION_GET_HTML';
  } else if (templates.asset) {
    const bodyAsset = JSON.parse(JSON.stringify(templates.asset));
    bodyAsset.logs = [];
    bodyAsset.category = templates.asset.category.name;
    body.asset = bodyAsset;
    ///body.request = 'DO_ACTION_GET_ASSET_HTML';
  } else if (templates.inventory) {
    const bodyInventory = JSON.parse(JSON.stringify(templates.inventory));
    bodyInventory.logs = [];
    bodyInventory.category = templates.inventory.category.name;
    body.inventory = bodyInventory;
    ///body.request = 'DO_ACTION_GET_INVENTORY_HTML';
  } else return;
  if (!customTemplateType.html) body.lan = language;
  return body;
};
const getDate = (question, timezone) => {
  if (question.date) {
    question.dateFormat.replace('dd', 'DD');
    return moment(question.date).tz(timezone).format('DD/MM/YYYY hh:mm');
  }
  return '';
};
const EXIF_SUPPORTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/tiff',
]);
const OrientedImage = ({ src }) => {
  const [orientedUrl, setOrientedUrl] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const mimeType = (blob.type || '').toLowerCase();
        if (EXIF_SUPPORTED_MIME_TYPES.has(mimeType)) {
          const fixedUrl = await fixImageOrientation(blob);
          setOrientedUrl(fixedUrl);
        } else {
          const objectUrl = URL.createObjectURL(blob);
          setOrientedUrl(objectUrl);
        }
      } catch (err) {
        console.error('Error fixing image orientation', err);
        setOrientedUrl(src);
      }
    };

    if (src) fetchImage();
  }, [src]);
  if (!orientedUrl) return null;
  return <img style={{ maxWidth: '400px', maxHeight: '400px' }} src={orientedUrl} alt='' />;
};
export const getFieldQuestion = (question, showFormFile, timezone, lan, getTextFile, isField) => {
  try {
    let text = '';
    switch (parseInt(question.type)) {
      case Types.MULTIPLE_CHOICE:
        const selected = [];
        question.options.some((opt) => {
          if (opt.checked === true) {
            selected.push(opt.name);
            if (question.singleAnswer === true) return true;
          }
        });
        text = selected.join(', ');
        break;
      case Types.TEXT_FIELD:
        text = question.text;
        break;
      case Types.NUMERIC_RATING:
        text = question.value;
        break;
      case Types.BINARY_RATING:
        if (question.iconType === 'FINGERS') {
          question.value === true ? (text = likeIcon) : (text = dislikeIcon);
        } else question.value === true ? (text = happyIcon) : (text = sadIcon);
        break;
      case Types.STARS_RATING:
        text = question.value;
        break;
      case Types.ELECTRONIC_SIGNATURE:
        text = question.file;
        break;
      case Types.FILES:
        text = question.file;
        break;
      case Types.CODEBAR_IMG:
        text = question.file;
        break;
      case Types.CODEBAR_TEXT:
        text = question.value;
        break;
      case Types.DATE_HOUR:
        text = question.parsedDate ? question.parsedDate : getDate(question, timezone);
        if (text != null) {
          text = text.replace('January', lan.january);
          text = text.replace('February', lan.february);
          text = text.replace('March', lan.march);
          text = text.replace('April', lan.april);
          text = text.replace('May', lan.may);
          text = text.replace('June', lan.june);
          text = text.replace('July', lan.july);
          text = text.replace('August', lan.august);
          text = text.replace('September', lan.september);
          text = text.replace('October', lan.october);
          text = text.replace('November', lan.november);
          text = text.replace('December', lan.december);
        } else {
          text = '';
        }
        break;
      case Types.TEXT_FILE:
        if (question.file) {
          if (showFormFile[question.id]) {
            text = (
              <TextareaAutosize
                readOnly
                className='task-details-input'
                value={showFormFile[question.id]}
                style={{
                  width: '100%',
                  paddingTop: '5px',
                  paddingLeft: '10px',
                  paddingBottom: '5px',
                  paddingRight: '10px',
                }}
              />
            );
          } else {
            text = (
              <Button
                variant="outlined"
                size={'medium'}
                fullWidth
                onClick={()=>getTextFile(question, isField)}>
                {lan.viewFileContent}
              </Button>
            );
          }
        } else {
          text = <button className='Fielder-button-clear-disabled'>{lan.noFileFound}</button>;
        }
        break;
      case Types.CATALOGUE:
        const columns = [
          { id: 'id', padding: '' },
          { id: 'option', padding: '' },
        ];
        if (question.includeValue) {
          columns.push({
            id: 'value',
            name: lan.value,
            padding: '',
          });
        }
        if (question.includeComment) {
          columns.push({
            id: 'comment',
            name: lan.comment,
            padding: '',
          });
        }
        const rows = question.options
          .map((opt) => {
            if (opt.checked) {
              return {
                id: opt.id,
                option: opt.name,
                value: opt.value ? opt.value : '',
                comment: opt.comment ? opt.comment : '',
              };
            }
          })
          .filter((n) => n);
        return (
          <Grid container className='mt-4'>
            <Grid size={10} className='ml-auto mr-5'>
              <Grid container>
                <Grid size={3}>{question.title}</Grid>
                <Grid size={9} className='dashed-box-border p-3'>
                  {rows && rows.length > 0 && (
                    <MainTable dense disabled fullItemsHeight cols={columns} rows={rows} lan={lan} height={500} />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      default:
        break;
    }
    if (text === null) text = '';
    if (parseInt(question.type) === Types.ELECTRONIC_SIGNATURE || parseInt(question.type) === Types.CODEBAR_IMG) {
      return (
        <Grid container className='mt-4'>
          <Grid size={10} className='ml-auto mr-5'>
            <Grid container>
              <Grid size={3}>{question.title}</Grid>
              <Grid size={9}>
                <div className='dashed-box-border'>
                  {text !== '' ? (
                    <img
                      src={text}
                      className='mx-auto d-block my-4'
                      style={{ maxWidth: '100%', maxHeight: '400px' }}
                      alt=''
                    />
                  ) : (
                    ''
                  )}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    if (parseInt(question.type) === Types.FILES) {
      return (
        <Grid container className='mt-4'>
          <Grid size={10} className='ml-auto mr-5'>
            <Grid container>
              <Grid style={{ height: '400px' }} size={3}>
                {question.title}
              </Grid>
              <Grid style={{ height: '400px' }} size={9}>
                <div className='task-details-input-photo' style={{ height: '400px' }}>
                  {text !== '' ? <OrientedImage src={text} /> : ''}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    if (parseInt(question.type) === Types.STARS_RATING) {
      const stars = [];
      for (let i = 0; i < 5; i++) {
        if (i < text) {
          stars.push(
            <div className='ml-2'>
              {' '}
              <img src={yellowStarIcon} style={{ maxHeight: '25px' }} alt='' />{' '}
            </div>
          );
        } else {
          stars.push(
            <div className='ml-2'>
              {' '}
              <img src={greyStarIcon} style={{ maxHeight: '25px' }} alt='' />{' '}
            </div>
          );
        }
      }
      return (
        <Grid container className='mt-4'>
          <Grid size={10} className='ml-auto mr-5'>
            <Grid container>
              <Grid size={3}>{question.title}</Grid>
              <Grid size={9}>
                <div
                  className='task-details-input-photo row ml-1'
                  style={{ maxWidth: '100%', paddingTop: '10px', paddingBottom: '10px' }}
                >
                  {stars}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    if (parseInt(question.type) === Types.BINARY_RATING) {
      return (
        <Grid container className='mt-4'>
          <Grid size={10} className='ml-auto mr-5'>
            <Grid container>
              <Grid size={3}>{question.title}</Grid>
              <Grid size={9}>
                <div
                  className='task-details-input-photo row ml-1'
                  style={{ maxWidth: '100%', paddingTop: '10px', paddingBottom: '10px' }}
                >
                  <div className='ml-3'>
                    <img src={text} style={{ maxHeight: '25px' }} alt='' />
                  </div>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    if (parseInt(question.type) === Types.TEXT_FILE) {
      return (
        <Grid container className='mt-4'>
          <Grid size={10} className='ml-auto mr-5'>
            <Grid container>
              <Grid size={3}>{question.title}</Grid>
              <Grid size={9}>
                <div className='ml-3'>{text}</div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container className='mt-4'>
        <Grid size={10} className='ml-auto mr-5'>
          <Grid container>
            <Grid size={3}>{question.title}</Grid>
            <Grid size={9}>
              <TextareaAutosize
                readOnly
                className='task-details-input'
                value={text}
                style={{
                  width: '100%',
                  paddingTop: '5px',
                  paddingLeft: '10px',
                  paddingBottom: '5px',
                  paddingRight: '10px',
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  } catch (error) {
    console.log(error)
  }
};
*/
